import React from 'react';
import { styled } from '@mui/material';

import { Autocomplete, Avatar, Badge, Box, Button, Form, IconButton, Stack, Text, TextInput } from 'shell/components';
import { COUNTRY_LIST, EN_PROFICIENCY_OPTIONS, PRONOUN_OPTIONS, TIME_ZONE_LIST } from 'utils/constants';

const DesktopEditProfile = ({
    loading,
    form,
    isDirty,
    errors,
    onChange,
    onSubmit,
    onCancel,
    onClickEditProfileImage,
}) => {
    return (
        <CustomStack spacing={4}>
            <Text variant={'sh2'} weight={'semibold'}>
                Edit Profile
            </Text>
            <Box>
                <Badge
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                        <CustomIconButton
                            aria-label={'change avatar'}
                            kind={'edit'}
                            size={'small'}
                            variant={'circle'}
                            onClick={onClickEditProfileImage}
                        />
                    }
                    overlap={'circular'}
                >
                    <Avatar size={'x-large'} user={form} />
                </Badge>
            </Box>
            <CustomForm onSubmit={onSubmit}>
                <TextInput
                    error={Boolean(errors.first_name)}
                    helperText={errors.first_name}
                    label={'First Name'}
                    required
                    value={form.first_name}
                    onChange={(e) => onChange('first_name', e.target.value)}
                />
                <TextInput
                    error={Boolean(errors.last_name)}
                    helperText={errors.last_name}
                    label={'Last Name'}
                    required
                    value={form.last_name}
                    onChange={(e) => onChange('last_name', e.target.value)}
                />
                <TextInput
                    label={'Preferred Name'}
                    value={form.display_name}
                    onChange={(e) => onChange('display_name', e.target.value)}
                />
                <TextInput
                    disabled={true}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    label={'Email'}
                    required
                    value={form.email}
                    onChange={(e) => onChange('email', e.target.value)}
                />
                <Autocomplete
                    label={'Pronouns'}
                    options={PRONOUN_OPTIONS.map(({ title, value }) => ({ key: value, value: title }))}
                    value={
                        PRONOUN_OPTIONS.map(({ title, value }) => ({ key: value, value: title })).find(
                            ({ key }) => key === form.pronoun
                        ) || null
                    }
                    onChange={(_, value) => onChange('pronoun', value)}
                />
                <Autocomplete
                    label={'English Proficiency'}
                    options={EN_PROFICIENCY_OPTIONS.map(({ title, value }) => ({ key: value, value: title }))}
                    value={
                        EN_PROFICIENCY_OPTIONS.map(({ title, value }) => ({ key: value, value: title })).find(
                            ({ key }) => key === form.english_proficiency
                        ) || null
                    }
                    onChange={(_, value) => onChange('english_proficiency', value)}
                />
                <Autocomplete
                    label={'Country'}
                    options={COUNTRY_LIST.map(({ name, code }) => ({ key: code, value: name }))}
                    value={
                        COUNTRY_LIST.map(({ name, code }) => ({ key: code, value: name })).find(
                            ({ key }) => key === form.country
                        ) || null
                    }
                    virtualized
                    onChange={(_, value) => onChange('country', value)}
                />
                <Autocomplete
                    label={'Timezone'}
                    options={TIME_ZONE_LIST.map(({ text }) => ({ key: text, value: text }))}
                    value={
                        TIME_ZONE_LIST.map(({ text }) => ({ key: text, value: text })).find(
                            ({ key }) => key === form.timezone
                        ) || null
                    }
                    virtualized
                    onChange={(_, value) => onChange('timezone', value)}
                />
                <ButtonStack direction={'row-reverse'} spacing={2}>
                    <Button disabled={!isDirty} loading={loading} type={'submit'}>
                        Save
                    </Button>
                    {isDirty && (
                        <Button disabled={loading} variant={'secondary'} onClick={onCancel}>
                            Cancel
                        </Button>
                    )}
                </ButtonStack>
            </CustomForm>
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5),
        maxWidth: '50rem',
    };
});

const CustomIconButton = styled(IconButton)(({ theme }) => {
    return {
        border: `3px solid ${theme.palette.ensightful.shades[0]}`,
    };
});

const CustomForm = styled(Form)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridGap: theme.spacing(4),
    };
});

const ButtonStack = styled(Stack)(() => {
    return {
        gridColumn: '1 / 3',
        marginLeft: 'auto',
    };
});

export default DesktopEditProfile;
